import { ChangeEvent, useMemo, useState } from "react";
import {
  HOURS_PER_WORK_YEAR,
  LOADED_COST_PER_POSITION,
  MODEL,
  MODEL_EXCLUSIONS,
  OT_MULTIPLIER,
  RFI,
  SDPD,
  manualMinutes,
  aiMinutes,
} from "./data";
import { C, fmtDuration, fmtInt, fmtMoney } from "./theme";

// ===================================================================
// Inputs
// ===================================================================

type Inputs = {
  totalReportsPerYear: number;
  adoptionPct: number;
  avgWords: number;
  pctWithInteractionAudio: number;
  avgInteractionMin: number;
  loadedCostPerPosition: number;
  overtimeMixPct: number;
  reportsPerShift: number;
};

const DEFAULTS: Inputs = {
  // SDPD states 400,000+ calls for service annually. Not every call produces a
  // written report; 40% is a deliberately conservative placeholder that SDPD
  // should replace with its actual report volume.
  totalReportsPerYear: 160_000,
  adoptionPct: 70,
  // Average report length across the three agencies in Section 08 runs 596-719
  // words. 400 is deliberately well below that: it is the length a skeptical
  // reader would pick, and it makes the modeled saving smaller, not larger.
  avgWords: 400,
  pctWithInteractionAudio: 35,
  avgInteractionMin: 20,
  loadedCostPerPosition: LOADED_COST_PER_POSITION,
  overtimeMixPct: 25,
  reportsPerShift: 3,
};

// ===================================================================
// Model
// ===================================================================

function compute(i: Inputs) {
  // manualMinutes is linear in interaction-audio minutes, so weighting the
  // input by the share of reports that carry audio is equivalent to modeling
  // two cohorts separately and blending the results.
  const blendedInteractionMin =
    (i.pctWithInteractionAudio / 100) * i.avgInteractionMin;

  const manualMin = manualMinutes(i.avgWords, blendedInteractionMin);
  const aiMin = aiMinutes();
  const savedMin = Math.max(0, manualMin - aiMin);

  const aiReports = i.totalReportsPerYear * (i.adoptionPct / 100);
  const totalHours = (aiReports * savedMin) / 60;
  const fteEquivalent = totalHours / HOURS_PER_WORK_YEAR;

  const hourlyLoaded = i.loadedCostPerPosition / HOURS_PER_WORK_YEAR;
  const straightValue = totalHours * hourlyLoaded;
  // Portion of recovered time that would otherwise have been paid at the FLSA
  // overtime rate; post-shift report writing is compensable.
  const blendedValue =
    totalHours *
    hourlyLoaded *
    (1 + (i.overtimeMixPct / 100) * (OT_MULTIPLIER - 1));

  const perShiftMin = i.reportsPerShift * savedMin;

  return {
    manualMin,
    aiMin,
    savedMin,
    aiReports,
    totalHours,
    fteEquivalent,
    hourlyLoaded,
    straightValue,
    blendedValue,
    perShiftMin,
    blendedInteractionMin,
  };
}

// ===================================================================
// Calculator
// ===================================================================

export default function SavingsCalculator() {
  const [inputs, setInputs] = useState<Inputs>(DEFAULTS);
  const r = useMemo(() => compute(inputs), [inputs]);
  const set = <K extends keyof Inputs>(k: K) => (v: number) =>
    setInputs((prev) => ({ ...prev, [k]: v }));

  const isDefault = useMemo(
    () => (Object.keys(DEFAULTS) as (keyof Inputs)[]).every((k) => inputs[k] === DEFAULTS[k]),
    [inputs]
  );

  return (
    <div>
      <div className="grid lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-6 items-start">
        {/* ---------------- Inputs ---------------- */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 lg:sticky lg:top-16 print:static">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
              Your numbers
            </h3>
            {!isDefault && (
              <button
                type="button"
                onClick={() => setInputs(DEFAULTS)}
                className="text-[11px] text-blue-700 hover:text-blue-900 underline underline-offset-2 print:hidden"
              >
                Reset
              </button>
            )}
          </div>

          <div className="space-y-4">
            <NumberInput
              label="Reports written per year"
              value={inputs.totalReportsPerYear}
              onChange={set("totalReportsPerYear")}
              min={0}
              format="comma"
              hint={`SDPD reports ${fmtInt(
                RFI.callsForServicePerYear
              )}+ calls for service annually. This default assumes roughly 40% produce a written report. Replace it with your actual volume.`}
            />
            <NumberInput
              label="Adoption"
              value={inputs.adoptionPct}
              onChange={set("adoptionPct")}
              min={0}
              max={100}
              format="percent"
              hint="Share of those reports written with the platform. Savings scale directly with this number."
            />
            <NumberInput
              label="Average words per report"
              value={inputs.avgWords}
              onChange={set("avgWords")}
              min={0}
              format="comma"
              hint="Across the three departments in Section 08 this ran 596–719 words. The default here is deliberately lower."
            />
            <NumberInput
              label="Reports with interview or body-cam audio"
              value={inputs.pctWithInteractionAudio}
              onChange={set("pctWithInteractionAudio")}
              min={0}
              max={100}
              format="percent"
              hint="These reports save the most, because the officer no longer re-listens to the recording."
            />
            <NumberInput
              label="Average audio minutes on those reports"
              value={inputs.avgInteractionMin}
              onChange={set("avgInteractionMin")}
              min={0}
              format="comma"
            />
            <NumberInput
              label="Fully-loaded cost per position"
              value={inputs.loadedCostPerPosition}
              onChange={set("loadedCostPerPosition")}
              min={0}
              format="dollar"
              hint={`Derived from the FY2026 Adopted Budget: salary and wages plus fringe benefits, divided by total FTE. Averages sworn and civilian classifications; substitute a sworn-only figure if you have one.`}
            />
            <NumberInput
              label="Recovered time that would have been overtime"
              value={inputs.overtimeMixPct}
              onChange={set("overtimeMixPct")}
              min={0}
              max={100}
              format="percent"
              hint={`Report writing that runs past end of shift is FLSA-compensable at ${OT_MULTIPLIER}×.`}
            />
          </div>
        </div>

        {/* ---------------- Results ---------------- */}
        <div className="space-y-6 min-w-0">
          {/* Headline tiles */}
          <div className="grid sm:grid-cols-3 gap-3">
            <ResultTile
              label="Hours returned per year"
              value={fmtInt(r.totalHours)}
              unit="hr"
              tone="primary"
            />
            <ResultTile
              label="Equivalent officers"
              value={r.fteEquivalent.toFixed(1)}
              unit="FTE"
              sub={`at ${fmtInt(HOURS_PER_WORK_YEAR)} hr/yr`}
              tone="good"
              accent
            />
            <ResultTile
              label="Labor value recovered"
              value={fmtMoney(r.blendedValue)}
              sub={`${fmtMoney(r.straightValue)} at straight time`}
              tone="primary"
            />
          </div>

          {/* Per-report comparison */}
          <div className="rounded-lg border border-slate-200 p-5">
            <h4 className="text-sm font-semibold text-slate-900 mb-1">
              One report, before and after
            </h4>
            <p className="text-xs text-slate-600 mb-4 max-w-xl">
              A {fmtInt(inputs.avgWords)}-word report carrying{" "}
              {r.blendedInteractionMin.toFixed(0)} minutes of interview or body-cam audio
              on average.
            </p>
            <BeforeAfterBars manualMin={r.manualMin} aiMin={r.aiMin} savedMin={r.savedMin} />
          </div>

          {/* Per officer per shift */}
          <div className="rounded-lg border border-slate-200 p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
              <h4 className="text-sm font-semibold text-slate-900">
                What one officer gets back
              </h4>
              <div className="flex items-center gap-2 print:hidden">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  Reports per shift
                </span>
                <div className="flex rounded-md border border-slate-300 overflow-hidden">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => set("reportsPerShift")(n)}
                      className={`w-8 py-1 text-sm font-semibold tabular-nums transition-colors ${
                        inputs.reportsPerShift === n
                          ? "bg-blue-600 text-white"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-600 mb-4">
              An officer who writes {inputs.reportsPerShift} report
              {inputs.reportsPerShift === 1 ? "" : "s"} in a shift.
            </p>
            <div className="flex items-baseline gap-3">
              <span
                className="text-4xl md:text-5xl font-bold tabular-nums leading-none"
                style={{ color: C.good }}
              >
                {fmtDuration(r.perShiftMin)}
              </span>
              <span className="text-sm text-slate-600">back, every shift</span>
            </div>
            <p className="text-xs text-slate-500 mt-3 max-w-xl">
              This is per-officer, not a department average. Most sworn personnel do
              not write reports on every shift. The department-level figures above
              already account for that by working from total report volume.
            </p>
          </div>

        </div>
      </div>

      {/* ---------------- Methodology ---------------- */}
      <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-5">
        <h4 className="text-sm font-semibold text-slate-900 mb-3">
          How this is calculated, and what it leaves out
        </h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
              The model
            </div>
            <pre className="text-[11px] leading-relaxed font-mono bg-white border border-slate-200 rounded-md p-3 overflow-x-auto text-slate-800">
{`manual  = ${MODEL.setupMinPerReport} min
        + words ÷ ${MODEL.typingWpm} wpm
        + audio_min ÷ ${MODEL.relistenSpeed}

with AI = ${MODEL.reviewMinPerCase} min review

saved   = manual − with AI`}
            </pre>
            <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">
              The {MODEL.setupMinPerReport}-minute fixed overhead covers pulling up the
              case, running subjects, gathering evidence, moving between the RMS and
              the editor, applying agency formatting, and sign-off. The constants are
              calibrated so a ~360-word report models to about 45 minutes, which
              matches what departments report. Dictation audio is deliberately absent
              from the formula, because speaking a report replaces typing it, so it is not
              additional work. Only interview and body-cam audio adds re-listening
              time.
            </p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
              Deliberately excluded
            </div>
            <ul className="space-y-1.5">
              {MODEL_EXCLUSIONS.map((x) => (
                <li key={x} className="flex gap-2 text-[11px] text-slate-700 leading-relaxed">
                  <span className="text-slate-400 shrink-0 mt-0.5">·</span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-600 mt-3 leading-relaxed">
              Every one of those would increase the figure. They are left out because
              they cannot be measured directly from platform data, which makes the
              result deliberately conservative.
            </p>
            <p className="text-[11px] text-slate-700 mt-3 leading-relaxed font-medium">
              These are modeled figures, not a stopwatch study, and they represent
              labor recovered, not cash returned to the General Fund. The value of
              recovered time is realized as officer availability.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// Pieces
// ===================================================================

function ResultTile({
  label,
  value,
  unit,
  sub,
  accent,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  tone?: "primary" | "good";
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-4 border ${
        accent
          ? "bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200"
          : "bg-white border-slate-200"
      }`}
    >
      <div
        className={`text-[11px] uppercase tracking-wider font-semibold ${
          accent ? "text-emerald-700" : "text-slate-500"
        }`}
      >
        {label}
      </div>
      <div
        className={`mt-1.5 text-3xl font-bold tabular-nums leading-tight ${
          accent ? "text-emerald-700" : "text-slate-900"
        }`}
      >
        {value}
        {unit && <span className="text-base font-medium ml-1 opacity-70">{unit}</span>}
      </div>
      {sub && (
        <div className={`text-[11px] mt-1 ${accent ? "text-emerald-700/70" : "text-slate-500"}`}>
          {sub}
        </div>
      )}
    </div>
  );
}

function BeforeAfterBars({
  manualMin,
  aiMin,
  savedMin,
}: {
  manualMin: number;
  aiMin: number;
  savedMin: number;
}) {
  const max = Math.max(manualMin, 1);
  const rows = [
    { label: "Written manually", min: manualMin, color: C.manual, ink: "#334155" },
    { label: "With Police Narratives AI", min: aiMin, color: C.primary, ink: "#ffffff" },
  ];
  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const pct = (row.min / max) * 100;
        return (
          <div key={row.label}>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-xs font-medium text-slate-700">{row.label}</span>
              <span className="text-sm font-bold tabular-nums text-slate-900">
                {fmtDuration(row.min)}
              </span>
            </div>
            <div className="h-7 bg-slate-100 rounded-md overflow-hidden">
              <div
                className="h-full rounded-md transition-all duration-700 ease-out"
                style={{ width: `${Math.max(pct, 1.5)}%`, backgroundColor: row.color }}
              />
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3 pt-2 mt-1 border-t border-slate-100">
        <span
          className="text-[11px] uppercase tracking-wider font-bold"
          style={{ color: C.good }}
        >
          Saved
        </span>
        <div className="flex-1 h-2 bg-emerald-50 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{ width: `${(savedMin / max) * 100}%`, backgroundColor: C.good }}
          />
        </div>
        <span className="text-sm font-bold tabular-nums" style={{ color: C.good }}>
          {fmtDuration(savedMin)}
        </span>
      </div>
    </div>
  );
}

export function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
  hint,
  format,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  hint?: string;
  format?: "dollar" | "percent" | "comma";
}) {
  const handle = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    const n = raw === "" ? 0 : parseFloat(raw);
    if (Number.isFinite(n)) {
      onChange(Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n)));
    }
  };
  const display =
    format === "dollar" || format === "comma" ? value.toLocaleString("en-US") : String(value);
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">
        {label}
      </div>
      <div className="relative">
        {format === "dollar" && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold pointer-events-none">
            $
          </span>
        )}
        {format === "percent" && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold pointer-events-none">
            %
          </span>
        )}
        <input
          type="text"
          inputMode="numeric"
          value={display}
          onChange={handle}
          className={`w-full ${format === "dollar" ? "pl-7" : "pl-3"} ${
            format === "percent" ? "pr-8" : "pr-3"
          } py-2 bg-white border border-slate-300 rounded-md text-base font-bold tabular-nums text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
        />
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">{hint}</div>}
    </label>
  );
}

/** Exposed so the page can quote the default-scenario headline in prose. */
export function defaultScenario() {
  return compute(DEFAULTS);
}

export { DEFAULTS as CALCULATOR_DEFAULTS, SDPD as SDPD_FACTS };
